import { useEffect, useState, useRef, useCallback } from 'react';
import { Circle, Polygon, Marker, Tooltip, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { getAccessToken } from '../utils/auth';
import { getNotamsInBounds } from '../utils/httpApi';
import { debugLog } from '../utils/debug';

// NOTAM category colors (consistent with Notams.js — teal for 'other', not purple)
const NOTAM_COLORS = {
  closure:  { fill: '#e74c3c', stroke: '#c0392b' },    // Red - closures (RWY, TWY, APT)
  obstacle: { fill: '#FF6B35', stroke: '#cc5529' },    // Orange - obstacles
  airspace: { fill: '#D4A520', stroke: '#b8901a' },    // Gold - airspace restrictions/TFR
  navaid:   { fill: '#3b9dff', stroke: '#2b7acc' },    // Blue - navaids
  other:    { fill: '#20c997', stroke: '#1aa37a' }     // Teal - other
};

/** Classify a NOTAM by its Q-code / text to determine display color */
function classifyNotam(notam) {
  const code = (notam.selectionCode || '').toUpperCase();
  const text = (notam.notamText || '').toUpperCase();

  // Closures: QMR (runway), QMX (taxiway), QMA (apron), QFALC (aerodrome closure)
  if (code.startsWith('QMR') || code.startsWith('QMX') || code.startsWith('QMA') ||
      code.startsWith('QFAL') || text.includes('CLSD') || text.includes('CLOSED')) {
    return 'closure';
  }
  // Obstacles
  if (code.startsWith('QOB') || code.startsWith('QOL') || text.includes('OBST') || text.includes('CRANE')) {
    return 'obstacle';
  }
  // Airspace / TFR
  if (code.startsWith('QR') || code.startsWith('QA') || text.includes('TFR') ||
      text.includes('RESTRICTED') || text.includes('PROHIBITED') || text.includes('AIRSPACE')) {
    return 'airspace';
  }
  // Navaids
  if (code.startsWith('QN') || text.includes('VOR') || text.includes('ILS') || text.includes('DME')) {
    return 'navaid';
  }
  return 'other';
}

/** Get short type label for map marker */
function getTypeLabel(notam) {
  const code = (notam.selectionCode || '').toUpperCase();
  const text = (notam.notamText || '').toUpperCase();

  if (code.startsWith('QMR') || text.includes('RWY')) return 'RWY';
  if (code.startsWith('QMX') || text.includes('TWY')) return 'TWY';
  if (code.startsWith('QMA') || text.includes('APRON')) return 'APN';
  if (code.startsWith('QFAL') || text.includes('AD CLSD')) return 'APT';
  if (code.startsWith('QOB') || text.includes('OBST')) return 'OBS';
  if (code.startsWith('QR') || text.includes('TFR')) return 'TFR';
  if (code.startsWith('QN') || text.includes('ILS') || text.includes('VOR')) return 'NAV';
  if (text.includes('SVC') || text.includes('SERVICE')) return 'SVC';
  return 'NTM';
}

/** Create numbered DivIcon for NOTAM marker */
function createNotamIcon(index, category) {
  const colors = NOTAM_COLORS[category] || NOTAM_COLORS.other;
  return L.divIcon({
    className: 'notam-marker-icon',
    html: `<div style="
      background: ${colors.fill};
      border: 2px solid ${colors.stroke};
      border-radius: 4px;
      color: white;
      font-size: 10px;
      font-weight: 700;
      width: 24px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-shadow: 0 1px 1px rgba(0,0,0,0.5);
      box-shadow: 0 1px 3px rgba(0,0,0,0.4);
    ">${index}</div>`,
    iconSize: [24, 18],
    iconAnchor: [12, 9]
  });
}

/** Format effective period for display */
function formatEffective(start, end) {
  const fmt = (dt) => {
    if (!dt) return '?';
    const d = new Date(dt);
    return d.toISOString().replace('T', ' ').substring(0, 16) + 'Z';
  };
  return `${fmt(start)} — ${end ? fmt(end) : 'PERM'}`;
}

/** Parse polygon coordinates from JSON string */
function parsePolygon(polygonStr) {
  if (!polygonStr) return null;
  try {
    // polygonCoordinates is stored as space-separated "lat lng lat lng ..." or JSON array
    if (polygonStr.startsWith('[')) {
      const arr = JSON.parse(polygonStr);
      // Array of [lat, lng] pairs
      if (Array.isArray(arr) && arr.length >= 3) return arr;
    }
    // Space-separated: "lat1 lng1 lat2 lng2 ..."
    const parts = polygonStr.trim().split(/\s+/).map(Number);
    if (parts.length >= 6 && parts.every(n => !isNaN(n))) {
      const coords = [];
      for (let i = 0; i < parts.length - 1; i += 2) {
        coords.push([parts[i], parts[i + 1]]);
      }
      if (coords.length >= 3) return coords;
    }
  } catch (e) {
    // Not parseable
  }
  return null;
}

function NotamOverlay({ pane = 'notamPane' }) {
  const map = useMap();
  const [notams, setNotams] = useState([]);
  const debounceTimerRef = useRef(null);
  const lastBoundsRef = useRef(null);

  const fetchNotams = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;

    const bounds = map.getBounds();
    const requestData = {
      minLat: bounds.getSouth(),
      maxLat: bounds.getNorth(),
      minLng: bounds.getWest(),
      maxLng: bounds.getEast()
    };

    // Skip if bounds haven't meaningfully changed
    const boundsKey = `${requestData.minLat.toFixed(2)},${requestData.maxLat.toFixed(2)},${requestData.minLng.toFixed(2)},${requestData.maxLng.toFixed(2)}`;
    if (lastBoundsRef.current === boundsKey) return;
    lastBoundsRef.current = boundsKey;

    try {
      const response = await getNotamsInBounds(requestData, token);
      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        debugLog('📋 Received NOTAMs:', response.notams?.length || 0);
        setNotams(response.notams || []);
      }
    } catch (error) {
      console.error('Error fetching NOTAMs:', error);
    }
  }, [map]);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(fetchNotams, 3000);
  }, [fetchNotams]);

  useMapEvents({
    moveend: debouncedFetch,
    zoomend: debouncedFetch
  });

  // Initial fetch
  useEffect(() => {
    const timer = setTimeout(fetchNotams, 1500);
    return () => clearTimeout(timer);
  }, [fetchNotams]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  if (!notams || notams.length === 0) return null;

  return (
    <>
      {notams.map((notam, idx) => {
        const category = classifyNotam(notam);
        const colors = NOTAM_COLORS[category] || NOTAM_COLORS.other;
        const typeLabel = getTypeLabel(notam);
        const polygon = parsePolygon(notam.polygonCoordinates);
        const hasCenter = notam.centerLatitude != null && notam.centerLongitude != null;
        const hasRadius = hasCenter && notam.radiusNm > 0;

        const tooltipContent = `${notam.notamId} [${typeLabel}] ${notam.icaoLocation || ''} — ${(notam.notamText || '').substring(0, 80)}`;
        const popupContent = (
          <div style={{ maxWidth: 300, fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{notam.notamId} — {notam.icaoLocation} {notam.airportName ? `(${notam.airportName})` : ''}</div>
            <div style={{ marginBottom: 4 }}>{notam.notamText}</div>
            <div style={{ color: '#888', fontSize: 11 }}>{formatEffective(notam.effectiveStart, notam.effectiveEnd)}</div>
          </div>
        );

        const elements = [];

        // Render polygon if available
        if (polygon) {
          elements.push(
            <Polygon
              key={`poly-${notam.notamId}`}
              positions={polygon}
              pathOptions={{
                color: colors.stroke,
                fillColor: colors.fill,
                fillOpacity: 0.15,
                weight: 2,
                dashArray: '6 4'
              }}
              pane={pane}
            >
              <Tooltip sticky>{tooltipContent}</Tooltip>
              <Popup>{popupContent}</Popup>
            </Polygon>
          );
        }

        // Render circle for radius-based NOTAMs
        if (hasRadius && !polygon) {
          elements.push(
            <Circle
              key={`circle-${notam.notamId}`}
              center={[notam.centerLatitude, notam.centerLongitude]}
              radius={notam.radiusNm * 1852}
              pathOptions={{
                color: colors.stroke,
                fillColor: colors.fill,
                fillOpacity: 0.12,
                weight: 2,
                dashArray: '6 4'
              }}
              pane={pane}
            >
              <Tooltip sticky>{tooltipContent}</Tooltip>
              <Popup>{popupContent}</Popup>
            </Circle>
          );
        }

        // Center marker with type label
        if (hasCenter) {
          elements.push(
            <Marker
              key={`marker-${notam.notamId}`}
              position={[notam.centerLatitude, notam.centerLongitude]}
              icon={createNotamIcon(idx + 1, category)}
              pane={pane}
            >
              <Tooltip>{tooltipContent}</Tooltip>
              <Popup>{popupContent}</Popup>
            </Marker>
          );
        }

        return elements;
      })}
    </>
  );
}

export default NotamOverlay;
